// import { HamburgerIcon, Menu } from 'lucide-react'
import { SetStateAction, useEffect, useRef, useState } from 'react'
import { Dispatch } from 'react'
import Link from 'next/link'
import { PanelLeftIcon, PowerIcon } from 'lucide-react'
import { useIntercom } from 'react-use-intercom'
import { cn } from '~/src/lib/utils'
import { useAuthContext } from '~/src/context/AuthProvider'
import useAuthUser from '~/src/hooks/useAuthUser'
import HeaderNavigation from '~/src/components/HeaderNavigation'
import {
  SheetTrigger,
  SheetClose,
  SheetContent,
} from '~/src/components/ui/sheet'
import { Sheet } from '~/src/components/ui/sheet'
import HamburgerIcon from '~/src/components/svg/HamburgerIcon'
import DupeLogo from '~/src/components/svg/DupeLogo'
import ProviderButton from '~/src/components/ProviderButton'
import { GoogleAuthProvider } from '~/src/lib/firebase/firebaseAuth'
import MenuRecents from '~/src/components/home/MenuRecents'
import DesktopSideNav from './DesktopSideNav'

interface Props {
  setIsSheetOpen: Dispatch<SetStateAction<boolean>>
  isSheetOpen: boolean
}

const HeaderSheetMenu: React.FC<Props> = ({ setIsSheetOpen, isSheetOpen }) => {
  const { signOut, authUserNotAnonymous } = useAuthUser()
  const { userData, openAuthModal } = useAuthContext()
  const { showNewMessage } = useIntercom()
  const [hasScrolled, setHasScrolled] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isSheetOpen) return

    let elem: HTMLElement | null = null

    const handleScroll = (event: Event) => {
      const target = event.target as HTMLElement
      const scrollY = target.scrollTop

      setHasScrolled(scrollY > 0)
    }

    const timer = requestAnimationFrame(() => {
      elem = scrollContainerRef.current
      elem?.addEventListener('scroll', handleScroll, { passive: true })
    })

    return () => {
      if (elem) {
        elem.removeEventListener('scroll', handleScroll)
      }
      cancelAnimationFrame(timer)
    }
  }, [isSheetOpen])

  return (
    <>
      {/* Desktop Side Nav - Only show on desktop */}
      <div className='hidden md:block'>
        <DesktopSideNav />
      </div>

      {/* Mobile Sheet Menu - Only show on mobile */}
      <div className='md:hidden'>
        <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
          <SheetTrigger asChild>
            <button
              className='relative flex items-center justify-center gap-0.5  p-1'
              aria-label='Menu'
            >
              <HamburgerIcon className='h-auto w-5 md:w-6' />
            </button>
          </SheetTrigger>
          <SheetContent
            side='left'
            hideXClose
            className='flex w-[300px] flex-col sm:w-[400px]'
          >
            <div
              ref={scrollContainerRef}
              className='no-scrollbar flex flex-1 flex-col overflow-auto'
            >
              <div
                className={cn(
                  'dark:bg-darkest sticky top-0 z-10 flex items-center justify-between border-b border-b-black/0 p-5 transition-colors',
                  {
                    'border-b-black/5 dark:border-b-white/5': hasScrolled,
                  }
                )}
              >
                <DupeLogo className='h-7 max-w-min md:h-8' />
                <SheetClose className='opacity-30 transition-opacity hover:opacity-50'>
                  <PanelLeftIcon className='h-5 w-5 md:h-6 md:w-6' />
                </SheetClose>
              </div>

              <div className='flex flex-col gap-4'>
                <HeaderNavigation isSheetOpen={isSheetOpen} />
                <MenuRecents />
              </div>

              <nav className='dark:bg-darkest sticky bottom-0 mt-auto flex flex-col items-start gap-2 border-t border-black/5 p-4 md:p-8 dark:border-white/5'>
                {authUserNotAnonymous ? (
                  <div className='flex w-full items-center justify-between gap-4'>
                    <Link
                      href='/profile'
                      className='truncate text-sm font-medium opacity-50 hover:underline md:text-base'
                    >
                      {userData?.email || userData?.phone || 'Profile'}
                    </Link>
                    <button
                      onClick={() => showNewMessage()}
                      className='text-sm font-medium opacity-50 hover:underline md:text-base'
                    >
                      Help
                    </button>
                    <button onClick={() => signOut()} className='opacity-50'>
                      <PowerIcon
                        className='h-4 w-4 md:h-5 md:w-5'
                        strokeWidth={2.5}
                      />
                    </button>
                  </div>
                ) : (
                  <div className='flex flex-col gap-4'>
                    <p className='text-sm opacity-50'>
                      Access the best deals, earn cash for using Dupe, save all
                      your searches!
                    </p>
                    <ProviderButton
                      size='md'
                      providerId={GoogleAuthProvider.PROVIDER_ID}
                      onClick={() => {
                        setIsSheetOpen(false)
                        setTimeout(() => {
                          openAuthModal({
                            source: 'side-nav-signup',
                          })
                        }, 150)
                      }}
                    >
                      Sign up
                    </ProviderButton>
                  </div>
                )}
              </nav>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  )
}

export default HeaderSheetMenu
